/**
 * What the blocklist screen does to the list before drawing it.
 *
 * React-free so the rules worth getting right can be tested without a DOM: how
 * a typed number is matched against a stored one, how loudly each source reads,
 * and which rows deserve a warning. The list arrives whole from the server - it
 * is the set of numbers one desk got tired of, measured in dozens - so searching
 * and narrowing happen here rather than in a query.
 */
import type { InboundBlock } from "../../../../lib/callBlocklist";
import type { StatusTone } from "../../../ui";

/** The sources this list can hold, in the order the filter offers them. */
export const INBOUND_BLOCK_SOURCES = ["spam", "manual"] as const;

export type InboundBlockSourceKey = (typeof INBOUND_BLOCK_SOURCES)[number];

/** The filter value meaning "do not narrow by this". */
export const ANY_SOURCE = "__any__";

/**
 * How loudly each source reads.
 *
 * Quieter than the do-not-call list's tones on purpose. Nothing here is a
 * complaint about us or somebody exercising a right; it is a desk saying "not
 * this one again", and a screen full of red would make an ordinary housekeeping
 * list look like a wall of incidents.
 */
const SOURCE_TONE: Record<string, StatusTone> = {
  spam: "wt",
  manual: "info",
};

export function sourceTone(source: string): StatusTone {
  return SOURCE_TONE[source] ?? "mut";
}

/**
 * The digits of a number, for comparing one against another.
 *
 * Both sides go through this. The stored value is always E.164
 * (`+37255512345`) and an operator searching for it types whatever is in front
 * of them - "5551 2345" off the call log, "+372 5551 2345" out of a mail,
 * "0037255512345" pasted from a spreadsheet. Matching the strings would find
 * none of those; matching the digits finds all of them.
 *
 * THE LEADING `00` IS DROPPED, and that one line is the difference between the
 * spreadsheet spelling working and not. `00` and `+` are the same thing said
 * two ways - both mean "what follows is a country code" - but only the `+` is
 * punctuation, so stripping non-digits leaves the `00` behind and the search
 * then looks for `0037255512345` inside `37255512345` and finds nothing. The
 * symptom is the worst answer this screen can give: "that number is not
 * blocked", about a number that is.
 *
 * Only ever applied to a leading pair, so a real number that happens to
 * contain `00` is untouched.
 */
export function phoneDigits(raw: string): string {
  return raw.replace(/\D+/g, "").replace(/^00/, "");
}

/** True when a typed query looks like somebody is searching for a number. */
function looksNumeric(q: string): boolean {
  return /\d/.test(q);
}

export interface BlocklistFilter {
  q: string;
  /** One source, or `ANY_SOURCE`. */
  source: string;
}

export const DEFAULT_BLOCKLIST_FILTER: BlocklistFilter = { q: "", source: ANY_SOURCE };

export function blocklistFilterActive(f: BlocklistFilter): boolean {
  return f.q.trim() !== "" || f.source !== ANY_SOURCE;
}

/**
 * One row's searchable text, minus the number.
 *
 * The note and the reason are in it because that is where an operator writes
 * WHY ("robot, third time today", "wrong number, keeps redialling"), and the
 * operator's own name is in it because "who blocked this" is the first question
 * asked when a customer says they cannot get through.
 */
function textOf(row: InboundBlock): string {
  return [row.party?.companyName, row.note, row.reason, row.createdByName]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

/** Does this row answer the query? */
export function matchesQuery(row: InboundBlock, q: string): boolean {
  const query = q.trim();
  if (!query) return true;

  if (looksNumeric(query)) {
    const digits = phoneDigits(query);
    // `includes`, not `endsWith`: a country code typed in front is a prefix and
    // a local number typed alone is a suffix, and both are the same search.
    if (digits && phoneDigits(row.value).includes(digits)) return true;
  }

  return textOf(row).includes(query.toLowerCase());
}

/** The rows to draw, in the order the server sent them (newest first). */
export function filterBlocklist(rows: InboundBlock[], f: BlocklistFilter): InboundBlock[] {
  return rows.filter((row) => {
    if (f.source !== ANY_SOURCE && row.source !== f.source) return false;
    return matchesQuery(row, f.q);
  });
}

/**
 * Is this entry blocking somebody we know?
 *
 * THE ONE NUMBER ON THIS SCREEN THAT IS A WARNING RATHER THAN A FACT. A blocked
 * caller fails silently by design: they hear a refusal, nobody's browser rings,
 * and no row appears in any folder anybody works. That is exactly right for a
 * sales robot and exactly wrong for a client, and nothing else in the CRM would
 * ever surface the mistake - the customer would simply stop being able to reach
 * us, and the first anybody heard of it would be a complaint through some other
 * channel weeks later.
 *
 * A client or lead link is the evidence, NOT the raw call count: an unknown
 * number that rang three times and was blocked on the third is the list working
 * as intended, and counting that as a warning would put a badge on nearly every
 * row and teach everybody to ignore it.
 */
export function blocksKnownParty(row: InboundBlock): boolean {
  const party = row.party;
  if (!party) return false;
  return !!(party.clientId || party.leadId);
}

/** How many entries are turning away somebody in the CRM. Drawn above the list,
 *  because it is the one figure here that asks for action rather than reporting
 *  what the desk did. */
export function knownPartyCount(rows: InboundBlock[]): number {
  return rows.filter(blocksKnownParty).length;
}
